import {
  baseIterableRequest,
  IterableEmbeddedManager,
  IterableEmbeddedSessionManager,
  trackEmbeddedClick,
  type IterableEmbeddedMessage,
} from '@iterable/web-sdk'
import { captureException } from '@sentry/react'
import { getAnnouncementPreview } from 'features/devTools/announcementOverrides'
import { APP_PACKAGE_NAME } from './constants'

/**
 * The SDK objects that outlive any one render.
 *
 * Both are stateful — the manager caches the last fetch and its update listeners, the session
 * manager accumulates impressions until `endSession` flushes them — so they are module
 * singletons rather than `useState` instances. A remount (tab switch, route change) would
 * otherwise start a fresh session and silently drop whatever impressions the old one held.
 *
 * The session manager is deliberately **not** exported: everything that reports to Iterable goes
 * through the wrappers below, which is what makes the Dev Tools preview switch unmissable.
 */
export const embeddedManager = new IterableEmbeddedManager(APP_PACKAGE_NAME)
const embeddedSessionManager = new IterableEmbeddedSessionManager(APP_PACKAGE_NAME)

/**
 * Nothing is reported to Iterable while a Dev Tools fixture is pinned.
 *
 * One switch here rather than a check at each call site. Fixture messages carry invented ids, so
 * reporting them would write rows against campaigns that do not exist and corrupt the only output
 * this feature has. Six separate guards all meaning the same thing is six chances for the seventh
 * tracked event to forget; a single gate in front of every reporting call cannot be forgotten.
 *
 * Read once per module load, matching the hook: the Dev Tools panel forces a reload whenever it
 * changes the pinned fixture, so there is nothing to invalidate.
 */
let previewActive: boolean | undefined

function isPreviewActive(): boolean {
  previewActive ??= getAnnouncementPreview().length > 0
  return previewActive
}

/** Test seam: drops the memoised preview state so each test starts clean. */
export function resetEmbeddedPreviewForTests(): void {
  previewActive = undefined
}

type EmbeddedSubscriber = { onUpdate: () => void; onDisabled: () => void }

const subscribers = new Set<EmbeddedSubscriber>()
let isListenerRegistered = false

/**
 * Subscribes to the manager's message updates, and returns an unsubscribe.
 *
 * The SDK's `addUpdateListener` has **no counterpart that removes one** (checked against v2.3.3:
 * the manager exposes `addUpdateListener` and `getUpdateHandlers`, and nothing else). Since the
 * manager is a module singleton, a component that registered directly would leave its listener
 * behind on every unmount — remounting the dashboard a few times during a shift would leave a
 * pile of dead listeners, each firing `setState` on an unmounted tree at every poll.
 *
 * So exactly one real listener is registered, for the lifetime of the module, and this fan-out
 * owns a set that components can actually leave.
 */
export function subscribeToEmbeddedMessages(subscriber: EmbeddedSubscriber): () => void {
  subscribers.add(subscriber)

  if (!isListenerRegistered) {
    isListenerRegistered = true
    embeddedManager.addUpdateListener({
      onMessagesUpdated: () => notifyAll((current) => current.onUpdate()),
      onEmbeddedMessagingDisabled: () => notifyAll((current) => current.onDisabled()),
    })
  }

  return () => {
    subscribers.delete(subscriber)
  }
}

/**
 * Notifies every subscriber, isolating each one.
 *
 * A bare `forEach` lets the first subscriber that throws stop the rest being told. With one
 * placement that cannot happen; it becomes real the day a second one subscribes, and the symptom
 * then is a placement that silently stops updating because of a bug in a different placement.
 */
function notifyAll(notify: (subscriber: EmbeddedSubscriber) => void): void {
  subscribers.forEach((subscriber) => {
    try {
      notify(subscriber)
    } catch (error) {
      captureException(error)
    }
  })
}

/**
 * Impression and session reporting.
 *
 * Thin wrappers over the session manager so that every path out to Iterable passes the preview
 * gate, and so the placement does not reach into an SDK singleton directly.
 */
export function startSession(): void {
  if (isPreviewActive()) return
  embeddedSessionManager.startSession()
}

export function endSession(): void {
  if (isPreviewActive()) return
  // `endSession` is the only one of these that is async, and it POSTs. Without the catch its
  // rejection surfaces as an unattributed unhandled rejection rather than an error anyone can
  // trace back to announcements.
  embeddedSessionManager.endSession().catch((error: unknown) => captureException(error))
}

export function startImpression(messageId: string, placementId: number): void {
  if (isPreviewActive()) return
  embeddedSessionManager.startImpression(messageId, placementId)
}

export function pauseImpression(messageId: string): void {
  if (isPreviewActive()) return
  embeddedSessionManager.pauseImpression(messageId)
}

/**
 * Runs an announcement button's action and records the click.
 *
 * The SDK bundles both halves in `handleButtonClick`, but that lives in `src/embedded/utils` and
 * is not re-exported from the package root, so the two public pieces are called directly:
 * `manager.click` routes the target through `IterableConfig`'s handlers, and `trackEmbeddedClick`
 * is the same request the SDK's own helper would have sent.
 *
 * Routing happens before tracking so a slow analytics write never delays the navigation.
 */
export function runButtonAction(message: IterableEmbeddedMessage, buttonIdentifier: string, targetUrl: string): void {
  if (isPreviewActive()) return
  try {
    embeddedManager.click(targetUrl)
  } catch (error) {
    captureException(error)
  }

  trackEmbeddedClick({
    messageId: message.metadata.messageId,
    buttonIdentifier,
    targetUrl,
    appPackageName: APP_PACKAGE_NAME,
  }).catch((error: unknown) => captureException(error))
}

/**
 * Records a dismissal against Iterable.
 *
 * Hand-rolled because `trackEmbeddedDismiss` exists inside the SDK but is **not re-exported from
 * the package root** — `src/events/index.ts` forwards the click, session and received trackers
 * and omits this one (checked against v2.3.3). `baseIterableRequest` *is* exported, so this goes
 * out through the SDK's own axios instance and therefore its JWT interceptor; it is the same
 * request the SDK would have made, not a parallel API client.
 *
 * Fire-and-forget on purpose: the message is already gone from the UI by the time this runs, and
 * a failed analytics write must not resurrect it or surface an error to the provider.
 */
export function trackDismiss(message: IterableEmbeddedMessage, buttonIdentifier: string): void {
  if (isPreviewActive()) return
  baseIterableRequest({
    method: 'POST',
    url: '/embedded-messaging/events/dismiss',
    data: {
      messageId: message.metadata.messageId,
      buttonIdentifier,
      createdAt: Date.now(),
      deviceInfo: {
        platform: 'Web',
        deviceId: window.navigator.userAgent,
        appPackageName: APP_PACKAGE_NAME,
      },
    },
  }).catch((error: unknown) => captureException(error))
}
